import type { TFunction } from 'i18next';
import { ApiError } from '../../../lib/api.js';
import { estElevationAnnulee, estElevationNonAcceptee } from '../../../lib/elevation.js';
import { raisonCorrespondance } from '../../../lib/correspondance.js';
import { raisonPlan } from '../../../lib/plan.js';
import {
  lotAnnule,
  lotScelle,
  raisonReprise,
  type EtatLot,
  type LotReprise,
} from '../../../lib/reprise.js';
import type { BadgeTone } from '@artosera/ui';

/**
 * Le message d'une erreur de la reprise, ou `null` quand il n'y a rien à dire
 * (la personne a fermé la boîte du second facteur). Les raisons nommées de
 * l'API ont leur phrase ; une erreur inconnue garde le message de l'API, qui
 * est déjà une phrase française.
 */
export function messageReprise(
  t: TFunction,
  erreur: unknown,
  /** `images` : un mandat qui manque se dit en « images », pas en « lot ». */
  contexte?: 'images',
): string | null {
  if (estElevationAnnulee(erreur)) return null;
  if (estElevationNonAcceptee(erreur)) return String(t('reprise.erreurs.mfa_step_up_requis'));
  const raison = raisonReprise(erreur);
  if (raison !== null) {
    if (contexte === 'images' && (raison === 'nature_hors_mandat' || raison === 'mandat_absent'))
      return String(t(`images.erreurs.${raison}`));
    return String(t(`reprise.erreurs.${raison}`));
  }
  const plan = raisonPlan(erreur);
  if (plan !== null) return String(t(`plan.erreurs.${plan}`));
  const correspondance = raisonCorrespondance(erreur);
  if (correspondance !== null) return String(t(`correspondance.erreurs.${correspondance}`));
  if (erreur instanceof ApiError) return erreur.message;
  return String(t('commun.erreurReseau'));
}

export const TON_ETAT_LOT: Record<EtatLot, BadgeTone> = {
  depose: 'neutral',
  profile: 'neutral',
  transforme: 'neutral',
  rapproche: 'neutral',
  essaye: 'reserved',
  pret: 'available',
  enfile: 'reserved',
  en_cours: 'reserved',
  termine: 'available',
  echoue: 'sold',
  annule: 'withdrawn',
  scelle: 'available',
};

/**
 * L'état que la liste et la fiche montrent d'un lot : le scellement et
 * l'annulation sont posés à côté de l'état du lot (`termine`, `echoue`), pas à
 * sa place.
 */
export function etatAfficheLot(lot: Pick<LotReprise, 'etat' | 'annulation' | 'scelleLe'>): {
  cle: string;
  ton: BadgeTone;
} {
  if (lotScelle(lot)) return { cle: 'reprise.etats.scelle', ton: TON_ETAT_LOT.scelle };
  if (lot.annulation?.etat === 'demandee' || lot.annulation?.etat === 'en_cours')
    return { cle: 'plan.annulation.badges.annulationEnCours', ton: 'reserved' };
  if (lotAnnule(lot)) return { cle: 'reprise.etats.annule', ton: TON_ETAT_LOT.annule };
  return { cle: `reprise.etats.${lot.etat}`, ton: TON_ETAT_LOT[lot.etat] };
}

/** Le dernier refus d'un travail, en clair : la phrase nommée, sinon celle de l'API, sinon le code. */
export function texteRaisonTravail(
  t: TFunction,
  travail: { raison?: string | null; erreur?: string | null },
): string | null {
  const raison = travail.raison ?? null;
  if (raison === null) return null;
  const nomme = String(t(`plan.progression.raisons.${raison}`, { defaultValue: '' }));
  if (nomme !== '') return nomme;
  if (travail.erreur) return travail.erreur;
  return String(t('plan.progression.brute', { code: raison }));
}

/** Les jours en `AAAA-MM-JJ` locaux, pour un champ de date. */
export function jourLocal(decalageJours: number): string {
  const date = new Date();
  date.setDate(date.getDate() + decalageJours);
  const mois = String(date.getMonth() + 1).padStart(2, '0');
  const jour = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear())}-${mois}-${jour}`;
}
